import { ApolloLink, execute, toPromise, createHttpLink, HttpLink } from '@apollo/client';
import { captureException } from '@sentry/browser';

const customFetch = (uri: string, options: RequestInit) => {
  const { operationName } = JSON.parse(options.body as string);
  return fetch(`${uri}/${operationName}`, options);
};

class GraphQLClient {
  private readonly httpLink: ApolloLink;

  constructor({ endpointUrl, isLiftReport }: { endpointUrl: string; isLiftReport?: boolean }) {
    this.httpLink = isLiftReport
      ? new HttpLink({
          uri: endpointUrl,
          credentials: 'include',
          headers: { Authorization: 'Basic ZGV2OmthaXplbjUwMg==' },
        })
      : createHttpLink({ uri: endpointUrl, fetch: customFetch, credentials: 'include' });
  }

  query = async (query: any, variables: Record<string, any> = {}): Promise<Record<string, any>> => {
    try {
      const result = await toPromise(
        execute(this.httpLink, {
          query,
          variables,
        }),
      );

      if (result.errors) {
        if ((result.errors[0] as any).status === 404) {
          return result.errors[0] as Record<string, any>;
        }

        throw new GraphQLOperationFailed({ type: (result.errors[0] as any).type, query, variables });
      }

      return result.data as Record<string, any>;
    } catch (e) {
      const err = e as any;
      if (/Response not successful: Received status code [0-9]{3}/.test(err.message)) {
        throw new GraphQLHttpError({ query, variables, response: err.response });
      }

      // FIXME: ErrorPresenterの方に全部任せたい
      captureException(err);
      throw err;
    }
  };
}

export class GraphQLQueryException extends Error {
  public name = 'GraphQLQueryException';
  public readonly message: string;
  public readonly query: any;
  public readonly variables: Record<string, any>;

  constructor({ message, query, variables }: { message: string; query: any; variables: Record<string, any> }) {
    super();

    this.message = message;
    this.query = query;
    this.variables = variables;
  }
}

export class GraphQLHttpError extends GraphQLQueryException {
  public readonly name = 'GraphQLHttpError';
  public readonly response: Response;

  constructor({ query, variables, response }: { query: any; variables: Record<string, any>; response: Response }) {
    super({
      message: `A GraphQL operation is failed because the HTTP response has an exceptional status code. (${response.status} ${response.statusText})`,
      query,
      variables,
    });

    this.response = response;
  }
}

export class GraphQLOperationFailed extends GraphQLQueryException {
  public readonly name = 'GraphQLOperationFailed';
  public readonly type: string;

  constructor({ type, query, variables }: { type: string; query: any; variables: Record<string, any> }) {
    super({
      message: `A GraphQL operation is failed because the request is incorrect. (type: ${type})`,
      query,
      variables,
    });

    this.type = type;
  }
}

export default GraphQLClient;
